import { inject, Injectable, signal } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { firstValueFrom, from } from 'rxjs';
import { UserInformation } from 'src/app/shared/models/user-information.model';

import { environment } from '../../../environments/environment';
import { ValidPages } from '../../shared/enums/valid-pages.enum';
import { WenConfiguration } from '../../shared/models/wen-configuration.model';
import { UserService } from '../services/user.service';
import { WenConfigurationService } from '../services/wen.service';

@Injectable()
export class UserStore {
  private readonly authenticationService = inject(OAuthService);
  private readonly userService = inject(UserService);
  private readonly wenConfigurationService = inject(WenConfigurationService);

  private readonly PURE_USER_ROLE = 'app_usage';
  private readonly PURE_USER_ROLE_CN = 'app_usage_CN';
  private readonly PURE_BACKOFFICE_ROLE = 'backoffice';

  // BatteryCare - Roles for BatteryCare users
  private readonly PURE_BATTERYCARE_ROLE = 'batterycare_usage';
  private readonly PURE_BATTERYCARE_ROLE_CN = 'batterycare_usage_CN';

  private _userInformation = signal<UserInformation | undefined>(undefined);
  readonly userInformation = this._userInformation.asReadonly();

  private _userPreferredLanguage = signal<string | undefined>(undefined);
  readonly userPreferredLanguage = this._userPreferredLanguage.asReadonly();

  private _hub = signal<string | undefined>(undefined);
  readonly hub = this._hub.asReadonly();

  private _isLoading = signal<boolean>(false);
  readonly isLoading = this._isLoading.asReadonly();

  async startAuthentication(): Promise<void> {
    const wenConfiguration = await firstValueFrom(from(this.wenConfigurationService.retrieveWenConfiguration()));
    this._hub.set(wenConfiguration.hub);
    return await this.authenticateUser(this.setAuthConfig(wenConfiguration));
  }

  private setAuthConfig(wenConfiguration: WenConfiguration): AuthConfig {
    const authConfig = environment.authConfig as AuthConfig;

    authConfig.clientId = authConfig.clientId ?? wenConfiguration.clientId;
    authConfig.issuer = authConfig.issuer ?? wenConfiguration.authServerUrl;
    authConfig.loginUrl = authConfig.loginUrl ?? `${wenConfiguration.authServerUrl}/authorize`;
    authConfig.logoutUrl = authConfig.logoutUrl ?? `${wenConfiguration.authServerUrl}/connect/endSession`;
    authConfig.customQueryParams = authConfig.customQueryParams ?? wenConfiguration.customQueryParams;

    return authConfig;
  }

  private async authenticateUser(authConfig: AuthConfig): Promise<void> {
    this._isLoading.set(true);
    this.authenticationService.setStorage(sessionStorage);
    this.authenticationService.configure(authConfig);

    await firstValueFrom(from(this.authenticationService.tryLogin()));

    if (!this.authenticationService.hasValidAccessToken()) {
      this.authenticationService.initCodeFlow(window.location.pathname + window.location.search);
    }

    const userInformation = await firstValueFrom(from(this.userService.getUserInformation()));
    this._userInformation.set(userInformation);

    this.authenticationService.setupAutomaticSilentRefresh();
    this._isLoading.set(false);
  }

  public checkUserHasAccess(page: ValidPages): boolean {
    const user = this.userInformation();

    if (!user) {
      return false;
    }

    switch (page) {
      case ValidPages.APPLICATION:
        if(this.hub() === 'CN'){
          return user.permissions.includes(this.PURE_USER_ROLE_CN);
        }else{
          return user.permissions.includes(this.PURE_USER_ROLE);
        }
      case ValidPages.BACKOFFICE:
        return user.permissions.includes(this.PURE_BACKOFFICE_ROLE);
      // BatteryCare - Check user permission to access the BatteryCare page
      case ValidPages.BATTERY_CARE:
        if(this.hub() === 'CN'){
          return user.permissions.includes(this.PURE_BATTERYCARE_ROLE_CN);
        } else {
          return user.permissions.includes(this.PURE_BATTERYCARE_ROLE);
        }
      default:
        return false;
    }
  }

  setUserPreferredLanguage(language: string): void {
    this._userPreferredLanguage.set(language);
  }

  public logout(): void {
    this.authenticationService.logOut();
  }
}
